import React, { useState, useEffect, useCallback } from "react";

import styles from "./ProjectManagerList.module.css";
import Toast from "./Toast";
import SearchableSelect from "./SearchableSelect";

function TrashIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path
        d="M5.833 6.667v8.75A1.25 1.25 0 0 0 7.083 16.667h5.834a1.25 1.25 0 0 0 1.25-1.25v-8.75M4.167 5h11.666M7.5 5V3.75c0-.69.56-1.25 1.25-1.25h2.5c.69 0 1.25.56 1.25 1.25V5M8.333 9.167v4.166M11.667 9.167v4.166"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

interface PMUser {
  userId: number;
  firstName?: string;
  lastName?: string;
  businessName?: string;
}

interface ProjectManagerModel {
  id: number;
  userId: number;
  clientId: number;
}

interface ProjectManagerListProps {
  clientId: number;
  users: PMUser[];
  api: any;
  hideHeader?: boolean;
}

export default function ProjectManagerList({ clientId, users, api, hideHeader }: ProjectManagerListProps) {
  const [managers, setManagers] = useState<ProjectManagerModel[]>([]);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: "success" | "error" } | null>(null);

  const basePath = "ClientProjectManagers";

  const resolveUserName = (userId: number): string => {
    const u = users.find((u) => u.userId === userId);
    if (!u) return `User #${userId}`;
    const full = `${u.firstName || ""} ${u.lastName || ""}`.trim();
    return full || u.businessName || `User #${userId}`;
  };

  const loadManagers = useCallback(async () => {
    if (!api || !clientId) { setManagers([]); return; }
    try {
      const data = await api.get(`${basePath}/${clientId}`);
      const list: ProjectManagerModel[] = Array.isArray(data) ? data : [];
      setManagers(list);
    } catch (err: any) {
      if (err?.status === 404) {
        setManagers([]);
      } else {
        console.error("Error loading prospect managers:", err);
      }
    }
  }, [api, clientId]);

  useEffect(() => {
    setManagers([]);
    loadManagers();
  }, [loadManagers]);

  const availableUsers = users.filter(
    (u) => !managers.some((m) => m.userId === u.userId),
  );

  const addManager = async () => {
    if (!api || !selectedUserId) return;
    setLoading(true);
    try {
      await api.post(basePath, {
        clientId,
        userId: Number(selectedUserId),
      });
      setSelectedUserId("");
      await loadManagers();
      setToast({ message: "Prospect manager added.", type: "success" });
    } catch (err) {
      console.error("Error adding prospect manager:", err);
      setToast({ message: "Failed to add prospect manager.", type: "error" });
    } finally {
      setLoading(false);
    }
  };

  const removeManager = async (recordId: number) => {
    if (!api) return;
    setLoading(true);
    try {
      await api.delete(`${basePath}/${recordId}`);
      await loadManagers();
      setToast({ message: "Prospect manager removed.", type: "success" });
    } catch (err) {
      console.error("Error removing prospect manager:", err);
      setToast({ message: "Failed to remove prospect manager.", type: "error" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles["pm-card"]}>
      {!hideHeader && (
        <div className={styles["pm-section-head"]}>
          <h3 className={styles["pm-section-head__title"]}>Prospect Managers</h3>
          <p className={styles["pm-section-head__subtitle"]}>
            Manage the prospect managers assigned to this client.
          </p>
        </div>
      )}

      {clientId > 0 ? (
        <>
          <div className={styles["pm-composer"]}>
            <div className={styles["pm-field"]}>
              <label className={styles["pm-label"]} htmlFor="pm-select">
                Add Prospect Manager
              </label>
              <SearchableSelect
                id="pm-select"
                className={styles["pm-control"]}
                options={availableUsers.map((u) => ({ value: u.userId, label: resolveUserName(u.userId) }))}
                value={selectedUserId ? Number(selectedUserId) : null}
                onChange={(val) => setSelectedUserId(val ? String(val) : "")}
                placeholder="Search users..."
                disabled={loading}
              />
            </div>

            <div className={styles["pm-actions"]}>
              <button
                type="button"
                className={styles["pm-btn"]}
                onClick={addManager}
                disabled={!selectedUserId || loading}
              >
                + Add
              </button>
            </div>
          </div>

          <div className={styles["pm-subheader"]}>
            <span>Prospect Managers</span>
          </div>

          {managers.length === 0 ? (
            <div className={styles["pm-empty"]}>
              <svg className={styles["pm-empty__icon"]} width="48" height="48" viewBox="0 0 48 48" fill="none" aria-hidden="true">
                <rect x="8" y="18" width="32" height="22" rx="3" stroke="#d1d5db" strokeWidth="2" />
                <path d="M18 18v-4a6 6 0 0 1 12 0v4" stroke="#d1d5db" strokeWidth="2" strokeLinecap="round" />
                <circle cx="24" cy="30" r="3" stroke="#d1d5db" strokeWidth="2" />
              </svg>
              <p className={styles["pm-empty__title"]}>No prospect managers assigned</p>
              <p className={styles["pm-empty__subtitle"]}>
                Assign a prospect manager to grant them access to this client's data.
              </p>
            </div>
          ) : (
            <div className={styles["pm-table-wrap"]}>
              <table className={styles["pm-table"]}>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th className={styles["pm-col-actions"]}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {managers.map((m) => (
                    <tr key={m.id}>
                      <td className={styles["pm-name"]}>{resolveUserName(m.userId)}</td>
                      <td>
                        <div className={styles["pm-row-actions"]}>
                          <button
                            type="button"
                            className={styles["pm-icon-btn"]}
                            onClick={() => removeManager(m.id)}
                            disabled={loading}
                            aria-label={`Remove ${resolveUserName(m.userId)}`}
                            title="Remove prospect manager"
                          >
                            <TrashIcon />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      ) : (
        <div className={styles["pm-empty"]}>
          Save the client before managing prospect managers.
        </div>
      )}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
}
